import { forwardRef, JSX, useImperativeHandle } from 'react';
import * as L from 'leaflet';
import 'leaflet.locatecontrol';
import { AttributionControl, FeatureGroup, LayerGroup, LayersControl, MapContainer, Polygon, TileLayer, ZoomControl, useMapEvents, GeoJSON } from 'react-leaflet';
import { TranslationService } from '../../../services/translation.service';
import './style.scss';
import CustomLocateControl from './components/LocateControl';
import type IPolygon from './models/polygon.model';
import type IBounds from './models/bounds.model';
import FilterControl from './components/FilterControl';
import type { IGeoJson } from './models/geojson.model';

export interface IMap {
  setView(latLng: L.LatLngExpression, zoom: number): void;
  fitBounds(toBounds: IBounds | undefined): void;
  invalidateSize(): void;
}

interface IMapProps {
  mapId?: string;
  zoomControl?: boolean;
  locateControl?: boolean;
  layerControl?: boolean;
  filterControl?: boolean;
  hideFilterControlOnDesktop?: boolean;
  ariaLabel: string;
  className?: string;
  markers?: JSX.Element[];
  clusteredMarkers?: JSX.Element[];
  extraMarkers?: JSX.Element[];
  layers?: JSX.Element[];
  bounds?: IBounds;
  maxBounds?: L.LatLngBoundsExpression;
  polygons?: IPolygon[];
  geoJsons?: IGeoJson[];
  center?: L.LatLngExpression;
  zoomLevel?: number;
  maxBoundsViscosity?: number;
  onZoomEnd?: (currentZoomLevel: number) => void;
  onFilterControlClick?: () => void;
}

export default forwardRef<IMap, IMapProps>(function Map(
  {
    mapId = undefined,
    zoomControl = true,
    locateControl = true,
    layerControl = false,
    filterControl = false,
    hideFilterControlOnDesktop = false,
    ariaLabel,
    className = '',
    markers = undefined,
    clusteredMarkers = undefined,
    extraMarkers = undefined,
    layers = undefined,
    bounds = undefined,
    maxBounds = undefined,
    polygons = undefined,
    geoJsons = undefined,
    center = undefined,
    zoomLevel = 14,
    maxBoundsViscosity = 0.0,
    onZoomEnd = undefined,
    onFilterControlClick = undefined,
  },
  ref
) {
  const urlTemplateDefault = 'https://osm.vasttrafik.se/styles/osm_vt_basic/{z}/{x}/{y}.png';
  const attribution = '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const attributionPrefix = '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>';

  function MapSetup() {
    const map = useMapEvents({
      zoomend: () => {
        onZoomEnd?.(map.getZoom());
        map.invalidateSize();
      },
    });

    useImperativeHandle(ref, () => ({
      setView(latLng: L.LatLngExpression, zoom: number) {
        map.setView(latLng, zoom);
      },
      fitBounds(toBounds: IBounds | undefined) {
        if (toBounds?.padding !== undefined && toBounds.bounds && toBounds.bounds.isValid()) {
          map.fitBounds(toBounds.bounds.pad(toBounds.padding));
        }
      },
      invalidateSize() {
        map.invalidateSize();
      },
    }));

    if (ariaLabel) {
      map.getContainer().setAttribute('aria-label', ariaLabel);
    }

    if (bounds && bounds.bounds.isValid()) {
      if (bounds.method === 'panTo') {
        map.setZoom(zoomLevel);
        map.panTo(bounds.bounds.getCenter());
      } else if (bounds.method === 'fitBounds') {
        map.fitBounds(bounds.bounds.pad(bounds.padding ?? 0));
      }
    }

    if (maxBounds) {
      map.fitBounds(maxBounds);
      map.setMaxBounds(maxBounds);
    }

    return null;
  }

  return (
    <MapContainer
      id={mapId}
      center={center || [57.69676, 11.99392]}
      zoom={zoomLevel}
      scrollWheelZoom={true}
      className={`vt-map text-center p-3 p-md-5 ${className}`}
      zoomControl={false}
      attributionControl={false}
      preferCanvas={false}
      maxBoundsViscosity={maxBoundsViscosity}
    >
      <TileLayer attribution={attribution} url={urlTemplateDefault} />
      <AttributionControl position="bottomright" prefix={attributionPrefix} />
      {extraMarkers && <FeatureGroup>{extraMarkers}</FeatureGroup>}
      {markers && <FeatureGroup>{markers}</FeatureGroup>}
      {clusteredMarkers?.map((x) => x)}
      {filterControl && <FilterControl onClick={onFilterControlClick} className={hideFilterControlOnDesktop ? 'd-lg-none' : ''} />}
      {locateControl && <CustomLocateControl position="bottomright" />}
      {zoomControl && <ZoomControl position="bottomright" zoomInTitle={TranslationService.get('common.map.zoomin')} zoomOutTitle={TranslationService.get('common.map.zoomout')} />}
      {layerControl && <LayersControl position="topright"></LayersControl>}
      {polygons?.map((x) => (
        <Polygon key={x.id} positions={x.latLngs} pathOptions={x.options} />
      ))}
      {layers && <LayerGroup>{layers}</LayerGroup>}
      {geoJsons?.map((x) => (
        <GeoJSON key={x.id} data={x.data} pathOptions={x.options} interactive={false} />
      ))}
      <MapSetup />
    </MapContainer>
  );
});
